import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { InfoDot } from '../components/InfoDot.js';
import { TERMS, type Term } from '../terminology.js';
import '../terminology.css';

/**
 * 术语页（C-167 A7）。
 *
 * ## 这一页存在的理由
 *
 * **产品页面标识对象，不解释对象。** 一个数字旁边写三行解释，那一屏就没法看了；
 * 而不写解释，看的人只能猜。此前没有这一页，于是解释只能挤在页面上，或者干脆没有。
 *
 * 所以：**页面上只出现名词，解释全在这里。**
 *
 * ## 出处只在这一页出现（C-162）
 *
 * 每条都带 ADR / 规范编号。**其他页面一律不出现判据编号**——
 * 编号是我们自己对账用的，客户看见一串 ADR-00xx 只会觉得这产品在自说自话。
 *
 * ## 每个名词旁边挂一个小 i，这一页自己就是那个组件的验证场
 *
 * A8 要求小 i「在术语页上自演示」。所以这里的 i 不是装饰：
 * **它读的是同一份 `TERMS` 数据**，i 里显示的和下面正文显示的是同一条。
 * 若哪天数据源改了而组件没跟上，这一页会当场露出来——
 * 一个名词的正文有解释、i 里没有。
 */

function TermCard({ entry }: { entry: Term }): ReactNode {
  return (
    <div className="term-card">
      <div className="term-head">
        {entry.term}
        {/* 自演示：这个 i 与正文读同一条数据 */}
        <InfoDot term={entry.term} />
      </div>
      <p className="term-def">{entry.definition}</p>
      {entry.formula !== undefined && (
        <p className="term-formula">
          <strong>分子</strong> {entry.formula.numerator}
          <br />
          <strong>分母</strong> {entry.formula.denominator}
          {entry.formula.note !== undefined && (
            <>
              <br />
              <span className="term-formula-note">{entry.formula.note}</span>
            </>
          )}
        </p>
      )}
      {entry.pitfall !== undefined && <p className="term-pitfall">易错 {entry.pitfall}</p>}
      <p className="term-source">出处 {entry.source}</p>
    </div>
  );
}

export function TerminologyPage(): ReactNode {
  const own = TERMS.filter((entry) => entry.kind === 'own');
  const metric = TERMS.filter((entry) => entry.kind === 'metric');

  return (
    <div className="term-page">
      <h1>名词与口径</h1>
      <p className="term-lead">
        产品里出现的专有名词都在这里。<strong>凡是行业或世界共识的词不收</strong>
        ——部门、岗位、人数、状态这类通用词不在此列，收了这份表就没人看了。
        <br />
        每个名词旁边有个小 <strong>i</strong>，悬停或按回车看解释；
        计算类的名词写明<strong>分子与分母</strong>。
      </p>

      <h2 className="term-group-title">本产品自有的名词（{own.length} 条）</h2>
      {own.map((entry) => (
        <TermCard key={entry.term} entry={entry} />
      ))}

      <h2 className="term-group-title">
        计算口径（{metric.length} 条）——每条都写明分子分母
      </h2>
      <p className="term-lead">
        这几条最容易被读反。<strong>算不出来不等于 0</strong>：分母缺数据时页面显示
        「待编制数据到位」，那不是一个 0，是一个还没有的数。
      </p>
      {metric.map((entry) => (
        <TermCard key={entry.term} entry={entry} />
      ))}

      <p className="term-lead" style={{ marginTop: '2.5rem' }}>
        <Link to="/">← 回到中枢</Link>
      </p>
    </div>
  );
}
